import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Settings, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logoutUser } from "../../store/slices/authSlice";
import { hasFeatureAccess, FEATURES, isEmployeePricingCatalogOnly, getUserRole, formatRoleLabel } from "../../config/roleFeatures";
import { getPendingOrderCount } from "../../services/orders-api";
import {
  ORDERS_PENDING_COUNT_REFRESH,
  isOrdersRoute,
} from "../../utils/ordersPendingCount";
import ShortLogo from "../../assets/short-logo.svg";
import Logo from "../../assets/logo.svg";
import LogoLoycom from "../../assets/logo_lycon.svg";
import ShortLogoLoycom from "../../assets/log_lycon.svg";
import LogoHandywelt from "../../assets/logo-handywelt.png";
import ShortLogoBoth from "../../assets/short-logo-both.svg";
import LogoBoth from "../../assets/logo-both.svg";
import "../../style/sidebar.css";

import DashboardIcon from "../../assets/dashboard-icon.svg";
import SalesIcon from "../../assets/sales-icon.svg";
import ProductsIcon from "../../assets/products-icon.svg";
import PosIcon from "../../assets/pos-icon.svg";
import FinanceIcon from "../../assets/finance-icon.svg";
import VendorsIcon from "../../assets/vendors-icon.svg";
import CustomersIcon from "../../assets/customers-icon.svg";
import OrdersIcon from "../../assets/order-icon.svg";
import EmpManagementIcon from "../../assets/emp-management-icon.svg";
import FinanceIconGodown from "../../assets/finance-icon-godown.svg";

import ActiveDashboardIcon from "../../assets/icons/ActiveDashboardIcon";
import ActiveSettingsIcon from "../../assets/icons/ActiveSettingsIcon";
import ActiveEmpManagementIcon from "../../assets/icons/ActiveEmpManagementIcon";
import ReturnPageActiveIcon from "../../assets/icons/ReturnPageActiveIcon";
import ActiveImeiBarcodeIcon from "../../assets/icons/ActiveImeiBarcodeIcon";
import ActiveVendorsIcon from "../../assets/icons/ActiveVendorsIcon";
import ActiveCustomersIcon from "../../assets/icons/ActiveCustomersIcon";
import ActiveProductsIcon from "../../assets/icons/ActiveProductsIcon";
import ActiveFinanceIcon from "../../assets/icons/ActiveFinanceIcon";
import ActiveSales from "../../assets/icons/ActiveSales";
import ActivePosIcon from "../../assets/icons/ActivePosIcon";
import ActiveOrderIcon from "../../assets/icons/ActiveOrderIcon";
import ActiveFinanceIconGodown from "../../assets/icons/ActiveFinanceIconGodown";

const formatPendingOrderBadge = (count) => {
  if (!count || count <= 0) return null;
  return count > 20 ? "20+" : String(count);
};

const Sidebar = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onCollapseChange,
}) => {
  const isLoycomDomain =
    window.location.hostname === process.env.REACT_APP_WEB_LOYCOM_URL;
  const isHandyweltDomain =
    window.location.hostname === process.env.REACT_APP_WEB_HANDYWELT_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const previousPathRef = useRef(location.pathname);
  const { isMobileURL, isAccessoriesURL, isPartsURL, isGodownURL, isHandyweltURL, isMobileLikeURL } = useAppSelector(
    (state) => state.domain
  );

  const getSidebarLogo = (collapsed) => {
    if (isHandyweltDomain || isHandyweltURL) {
      return LogoHandywelt;
    }
    if (isLoycomDomain) {
      return collapsed ? ShortLogoLoycom : LogoLoycom;
    }
    if (isMobileLikeURL || isGodownURL) {
      return collapsed ? ShortLogo : Logo;
    }
    return collapsed ? ShortLogoBoth : LogoBoth;
  };

  const getUserInitials = (user) => {
    if (!user) return "";
    if (user.name) {
      const nameParts = user.name.trim().split(" ");
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[
          nameParts.length - 1
        ].charAt(0)}`.toUpperCase();
      }
      return user.name.charAt(0).toUpperCase();
    }
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(
        0
      )}`.toUpperCase();
    }
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user.display_email) return user.display_email.charAt(0).toUpperCase();
    return "";
  };

  const getDisplayName = (user) => {
    if (!user) return "";
    if (user.name) return user.name;
    if (user.first_name && user.last_name)
      return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.display_email) return user.display_email.split("@")[0];
    return "";
  };

  const hasProfileImage = (user) => {
    return user && (user.profile_image || user.avatar || user.image);
  };

  const getProfileImageUrl = (user) => {
    if (!user) return null;
    return user.profile_image || user.avatar || user.image || null;
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) onCollapseChange(newCollapsedState);
  };

  const allMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: DashboardIcon,
      activeIcon: ActiveDashboardIcon,
      feature: FEATURES.DASHBOARD,
    },
    {
      id: "pos",
      label: "POS",
      icon: PosIcon,
      activeIcon: ActivePosIcon,
      feature: FEATURES.POS,
    },
    {
      id: "orders",
      label: "Orders",
      icon: OrdersIcon,
      activeIcon: ActiveOrderIcon,
      feature: FEATURES.ORDERS,
    },
    {
      id: "sales",
      label: "Sales",
      icon: SalesIcon,
      activeIcon: ActiveSales,
      feature: FEATURES.SALES,
    },
    {
      id: "finance",
      label: "Finance",
      icon: FinanceIcon,
      activeIcon: ActiveFinanceIcon,
      feature: FEATURES.FINANCE,
    },
    {
      id: "products",
      label: "Products",
      icon: ProductsIcon,
      activeIcon: ActiveProductsIcon,
      feature: FEATURES.PRODUCTS,
    },
    {
      id: "transfer_to_pos",
      label: "Transfer to POS",
      icon: FinanceIconGodown,
      activeIcon: ActiveFinanceIconGodown,
      feature: FEATURES.TRANSFER_TO_POS,
    },
    {
      id: "customers",
      label: "Customers",
      icon: CustomersIcon,
      activeIcon: ActiveCustomersIcon,
      feature: FEATURES.CUSTOMERS,
    },
    {
      id: "vendors",
      label: "Suppliers",
      icon: VendorsIcon,
      activeIcon: ActiveVendorsIcon,
      feature: FEATURES.VENDORS,
    },
    {
      id: "imei",
      label: "IMEI Log",
      icon: require("../../assets/imei-barcode-icon.svg"),
      activeIcon: ActiveImeiBarcodeIcon,
      feature: FEATURES.IMEI,
    },
    ...(!isAccessoriesURL && !isGodownURL
      ? [
        {
          id: "returns",
          label: "Returns",
          icon: require("../../assets/return-page-icon.svg"),
          activeIcon: ReturnPageActiveIcon,
          feature: FEATURES.RETURNS,
        },
      ]
      : []),
    {
      id: "employee-management",
      label: "Emp. Management",
      icon: EmpManagementIcon,
      activeIcon: ActiveEmpManagementIcon,
      feature: FEATURES.EMPLOYEE_MANAGEMENT,
    },
  ];

  // Filter menu items based on user role
  const userRole = getUserRole(user);

  const menuItems = allMenuItems.filter((item) =>
    hasFeatureAccess(userRole, item.feature) ||
    (item.id === "products" && hasFeatureAccess(userRole, FEATURES.PRICING_CATALOG))
  );

  const showOrdersPendingBadge = hasFeatureAccess(userRole, FEATURES.ORDERS);
  const pendingOrderBadge = formatPendingOrderBadge(pendingOrderCount);

  const fetchPendingOrderCount = useCallback(async () => {
    if (!isAuthenticated || !showOrdersPendingBadge) {
      setPendingOrderCount(0);
      return;
    }
    try {
      const data = await getPendingOrderCount();
      setPendingOrderCount(Number(data?.pending_orders) || 0);
    } catch (error) {
      console.error("Failed to fetch pending order count:", error);
    }
  }, [isAuthenticated, showOrdersPendingBadge]);

  useEffect(() => {
    fetchPendingOrderCount();
  }, [fetchPendingOrderCount]);

  useEffect(() => {
    if (!isAuthenticated || !showOrdersPendingBadge) return;

    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;
    const wasOnOrders = isOrdersRoute(previousPath);
    const isOnOrders = isOrdersRoute(currentPath);

    if (previousPath !== currentPath && wasOnOrders !== isOnOrders) {
      fetchPendingOrderCount();
    }

    previousPathRef.current = currentPath;
  }, [
    location.pathname,
    fetchPendingOrderCount,
    isAuthenticated,
    showOrdersPendingBadge,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !showOrdersPendingBadge) return undefined;

    const handleRefresh = () => fetchPendingOrderCount();
    window.addEventListener(ORDERS_PENDING_COUNT_REFRESH, handleRefresh);
    return () =>
      window.removeEventListener(ORDERS_PENDING_COUNT_REFRESH, handleRefresh);
  }, [fetchPendingOrderCount, isAuthenticated, showOrdersPendingBadge]);

  const handleMenuClick = (itemId) => {
    if (itemId === "products" && isEmployeePricingCatalogOnly(userRole)) {
      navigate("/pricing-catalog");
      return;
    }

    navigate(`/${itemId}`);
  };

  const isActiveRoute = (itemId) => {
    return (
      location.pathname === `/${itemId}` ||
      (itemId === "dashboard" && location.pathname === "/dashboard") ||
      (itemId === "products" &&
        (location.pathname.startsWith("/product/") ||
          location.pathname === "/brands" ||
          location.pathname === "/category" ||
          location.pathname === "/batches" ||
          location.pathname === "/purchases" ||
          location.pathname === "/low-stock" ||
          location.pathname === "/tags" ||
          location.pathname === "/transfer-history" ||
          location.pathname.startsWith("/transfer-history/") ||
          location.pathname.startsWith("/tags/") ||
          location.pathname === "/pricing-catalog")) ||
      (itemId === "customers" &&
        (location.pathname === "/customers" ||
          location.pathname.startsWith("/customer/") ||
          location.pathname === "/customers/print")) ||
      (itemId === "vendors" &&
        (location.pathname === "/vendors" ||
          location.pathname.startsWith("/vendor/"))) ||
      (itemId === "sales" &&
        (location.pathname === "/sales" ||
          location.pathname.startsWith("/sales/"))) ||
      (itemId === "finance" &&
        (location.pathname === "/finance" ||
          location.pathname.startsWith("/finance/"))) ||
      (itemId === "imei" &&
        (location.pathname === "/imei" ||
          location.pathname.startsWith("/imei/"))) ||
      // ✅ Orders Active Route
      (itemId === "orders" &&
        (location.pathname === "/orders" ||
          location.pathname.startsWith("/orders/"))) ||
      // ✅ Transfer to POS Active Route
      (itemId === "transfer_to_pos" &&
        (location.pathname === "/transfer_to_pos" ||
          location.pathname.startsWith("/transfer_to_pos/"))) ||
      // ✅ Returns Active Route
      (itemId === "returns" &&
        (location.pathname === "/returns" ||
          location.pathname.startsWith("/returns/register") ||
          location.pathname.startsWith("/returns/repair-reserve") ||
          location.pathname.startsWith("/returns/vendor")))
    );
  };

  const getIconComponent = (icon, isActive, id, activeIcon) => {
    // ✅ If icon is a React component (like CiRedo), return it directly
    if (React.isValidElement(icon)) {
      return isActive ? activeIcon : icon;
    }

    // Otherwise, it’s an SVG path (fallback)
    let iconSrc = icon;
    const ActiveIconComponent = activeIcon;

    if (icon && typeof icon === "object" && icon.default) {
      iconSrc = icon.default;
    }

    return (
      <>
        {isActive ? (
          <ActiveIconComponent
            fill={
              isPartsURL
                ? "var(--primary-color-parts)"
                : (isAccessoriesURL)
                  ? "var(--primary-color-acc)"
                  : (isGodownURL)
                    ? "var(--primary-color-godown)"
                    : "var(--primary-color-mobile)"
            }
          />
        ) : (
          <img
            src={iconSrc}
            alt="icon"
            className="nav-icon"
            style={{
              width: 20,
              height: 20,
              display: "block",
              objectFit: "contain",
            }}
          />
        )}
      </>
    );
  };

  return (
    <div
      className={`sidebar ${isCollapsed ? "sidebar-collapsed" : "sidebar-open"
        } ${isMobileMenuOpen ? "sidebar-mobile-open" : "sidebar-mobile-closed"}`}
    >
      <div className="sidebar-content">
        {/* Header */}
        <div
          className={`${isCollapsed ? "sidebar-header-collapsed" : "sidebar-header"
            }`}
        >
          <div className={`sidebar-logo${isHandyweltDomain || isHandyweltURL ? " sidebar-logo-handywelt" : ""}`}>
            {/* <img src={isCollapsed ? ShortLogo : Logo} alt="Logo" /> */}
            <img
              src={getSidebarLogo(isCollapsed)}
              alt={isHandyweltDomain || isHandyweltURL ? "Handywelt" : "Logo"}
            />

            <button
              className={`${isCollapsed
                  ? `sidebar-toggle-btn-collapsed${isPartsURL ? "-parts" : ""}${(isAccessoriesURL) ? "-acc" : ""
                  }${isGodownURL ? "-godown" : ""}`
                  : `sidebar-toggle-btn-open${isPartsURL ? "-parts" : ""}${(isAccessoriesURL) ? "-acc" : ""
                  }${isGodownURL ? "-godown" : ""}`
                }`}
              onClick={toggleSidebar}
            >
              {isCollapsed ? (
                <ChevronRight size={25} />
              ) : (
                <ChevronLeft size={25} />
              )}
            </button>
          </div>
        </div>

        {/* Navigation Body */}
        <nav className="sidebar-nav no-scrollbar">
          <ul className="nav-list">
            {menuItems.map((item) => {
              const isActive = isActiveRoute(item.id);
              return (
                <li key={item.id} className="nav-item">
                  <button
                    className={`${isPartsURL
                        ? "nav-link-parts"
                        : (isAccessoriesURL)
                          ? "nav-link-acc"
                          : (isGodownURL)
                            ? "nav-link-godown"
                            : "nav-link"
                      } ${isActive ? "active" : ""}`}
                    onClick={() => handleMenuClick(item.id)}
                  >
                    <span className="nav-icon">
                      {getIconComponent(
                        item.icon,
                        isActive,
                        item.id,
                        item.activeIcon
                      )}
                    </span>
                    {!isCollapsed && (
                      <span className="nav-text">{item.label}</span>
                    )}
                    {item.id === "orders" && pendingOrderBadge && (
                      <span
                        className={`nav-pending-badge${
                          isCollapsed ? " nav-pending-badge--collapsed" : ""
                        }`}
                      >
                        {pendingOrderBadge}
                      </span>
                    )}
                    <div className="nav-tooltip">{item.label}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Settings */}
          {hasFeatureAccess(userRole, FEATURES.SETTINGS) && (
            <div className="sidebar-settings">
              <button
                className={`${isPartsURL
                    ? "nav-link-parts"
                    : (isAccessoriesURL)
                      ? "nav-link-acc"
                      : "nav-link"
                  } ${isActiveRoute("settings") ? "active" : ""}`}
                onClick={() => handleMenuClick("settings")}
              >
                <>
                  <span className="nav-icon">
                    {isActiveRoute("settings") ? (
                      <ActiveSettingsIcon
                        fill={
                          isPartsURL
                            ? "var(--primary-color-parts)"
                            : (isAccessoriesURL)
                              ? "var(--primary-color-acc)"
                              : (isGodownURL)
                                ? "var(--primary-color-godown)"
                                : "var(--primary-color-mobile)"
                        }
                      />
                    ) : (
                      <Settings className="nav-icon" />
                    )}
                  </span>

                  {!isCollapsed && <span className="nav-text">Settings</span>}
                  <div className="nav-tooltip">Settings</div>
                </>
              </button>
            </div>
          )}

          {/* Admin Info */}
          <div className="sidebar-admin-info">
            <div className="admin-profile">
              <div
                className={`admin-avatar${isPartsURL ? "-parts" : ""}${(isAccessoriesURL) ? "-acc" : ""
                  }${isGodownURL ? "-godown" : ""}`}
              >
                {hasProfileImage(user) ? (
                  <img
                    src={getProfileImageUrl(user)}
                    alt="Profile"
                    className="avatar-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <span
                  className="avatar-text"
                  style={{ display: hasProfileImage(user) ? "none" : "flex" }}
                >
                  {getUserInitials(user)}
                </span>
              </div>
              {!isCollapsed && (
                <div className="admin-details">
                  <div className="admin-name">{getDisplayName(user)}</div>
                  <div className="admin-role">{formatRoleLabel(userRole)}</div>
                </div>
              )}
              <div className="admin-tooltip">
                <div className="tooltip-name">{getDisplayName(user)}</div>
                <div className="tooltip-email">
                  {user?.display_email || userRole}
                </div>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div className="signout-container">
            <button className="signout-link" onClick={handleLogout}>
              <LogOut className="signout-icon" />
              {!isCollapsed && <span className="signout-text">Log Out</span>}
              <div className="tooltip">Log Out</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
